/**
 * Client-side attribution for shop → conversion. Persists a single most-recent
 * touch (contact click, product click, promo/coupon interaction) in
 * sessionStorage with a 24-hour TTL, and mirrors touches durably to
 * `/api/public/shop-attribution` so vendor dashboards can measure conversion
 * even when the user closes the tab and returns later on a different device
 * once signed in.
 *
 * Every operation is guarded — storage may be unavailable (privacy mode, SSR,
 * iframe restrictions) and the network POST is fire-and-forget: never throws
 * and never blocks navigation.
 */

const KEY = "jb.shop.attr";
const SESSION_KEY = "jb.shop.attr.sid";
const TTL_MS = 24 * 60 * 60 * 1000;
const ENDPOINT = "/api/public/shop-attribution";

export type ShopAttributionMethod =
  "chat" | "share" | "report" | "product_click" | "promo" | "coupon" | "recommendation";

export type ShopAttribution = {
  shop_slug: string;
  method: ShopAttributionMethod;
  product_slug?: string | null;
  ts: number;
};

function safeRandomId(): string {
  try {
    const c: Crypto | undefined = typeof crypto !== "undefined" ? crypto : undefined;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    if (c && typeof c.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      c.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {
    /* noop */
  }
  return `sid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Stable per-browser id used to correlate touches with conversions
 * server-side. Not tied to a user account — only lives in localStorage so it
 * persists across sessions.
 */
export function getShopAttributionSessionId(): string {
  if (typeof window === "undefined") return "ssr-unknown-sid";
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing && existing.length >= 8) return existing;
    const fresh = safeRandomId();
    window.localStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return safeRandomId();
  }
}

function postJSON(body: unknown): void {
  if (typeof window === "undefined" || typeof fetch !== "function") return;
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* noop */
  }
}

export function recordShopAttribution(a: Omit<ShopAttribution, "ts">): void {
  if (typeof window === "undefined") return;
  try {
    const payload: ShopAttribution = { ...a, ts: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* noop */
  }
  // Mirror to server (never blocks navigation)
  postJSON({
    kind: "touch",
    session_id: getShopAttributionSessionId(),
    shop_slug: a.shop_slug,
    method: a.method,
    product_slug: a.product_slug ?? null,
  });
}

export function recordShopConversion(input: {
  order_id: string;
  order_total?: number | null;
}): void {
  const attr = getShopAttribution();
  if (!attr) return;
  postJSON({
    kind: "conversion",
    session_id: getShopAttributionSessionId(),
    shop_slug: attr.shop_slug,
    order_id: input.order_id,
    method: attr.method,
    order_total: input.order_total ?? null,
  });
}

export function getShopAttribution(): ShopAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ShopAttribution;
    if (!parsed || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > TTL_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearShopAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
